import { useEffect, useRef, useState } from 'react';
import { history } from 'umi';
import { getOriginalShortLinkFilter, URL_FILTER_KEY, URL_CHART_TYPE } from '@intelligence/utils/urlPathCfg';
import { getTopMobilesCustomValidUrlParams } from '@intelligence/_config/urlPageValidCfg';
import { getTopMobilesCustomValidUrlParams_Nikke } from '@intelligence/utils/topchartsUtils';
import { parseUrlFilterParams } from '@intelligence/v3/utils/urlFilterParamsAnalyser';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';

import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';
import type { IMultiDataSourceTopChartsPageConfig } from '../types';

export default function useMultiDataSourceTopChartsPage(
  pageConfig: IMultiDataSourceTopChartsPageConfig,
  props?: {
    loading?: boolean;
    genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  },
) {
  const { hasUrlShortLink, urlValidConfig, specialLogic } = pageConfig;
  const { loading = false, genreOptionsMap } = props || {};

  const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkLoading, setShortLinkLoading] = useState(hasUrlShortLink);
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({ [URL_CHART_TYPE]: 'bar' });
  const [shortLinkFilterValue, setShortLinkFilterValue] = useState<IRecord | null>(null);
  const prevFilterTagNameRef = useRef<string | undefined>(undefined);

  const initialize = async () => {
    try {
      setShortLinkLoading(true);
      const query = history.location.query || {};

      // 互斥逻辑：有 use_url_filter 标志时，执行新的解析逻辑，跳过其他默认筛选条件解析
      if (query?.use_url_filter) {
        // 使用新的 URL 参数解析逻辑（从 CasualGenreProfile 跳转）
        const { filterParams: urlFilterParams, customParams: urlCustomParamsParsed } = parseUrlFilterParams(query, {
          genreOptionsMap,
        });

        setUrlCustomParams({ ...urlCustomParamsParsed });
        setShortLinkFilterValue(urlFilterParams);
      } else {
        // 原有逻辑：shortLink 和 filter_tag_name
        const { [URL_FILTER_KEY]: urlFilter } = await getOriginalShortLinkFilter();

        if (urlFilter || query?.source === 'appannie') {
          const { params, validQuery } = getTopMobilesCustomValidUrlParams(history, urlValidConfig);
          setUrlCustomParams({ ...params });
          setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
          setShortLinkFilterValue(query?.source === 'appannie' ? {
            ...(urlFilter || {}),
            [filterKeyMap.DATASOURCE]: { value: ['sensortower', 'appannie'] },
          } : urlFilter);
        } else if (specialLogic?.nikkeLink) {
          const nikkeParams = getTopMobilesCustomValidUrlParams_Nikke(history);
          setUrlCustomParams({ ...nikkeParams });
        }

        // 有 filter_tag_name 时，跳过 analysisFilterParams（cond 请求）
        // 改由 HotTagsSource 筛选器匹配对应选项后通过 handleHotTagConfirm 应用筛选值
        prevFilterTagNameRef.current = query?.filter_tag_name as string | undefined;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setShortLinkLoading(false);
    }
  };

  useEffect(() => {
    if (loading) {
      return;
    }

    void initialize();
  }, [loading]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const query = history.location.query || {};
    const currentFilterTagName = query?.filter_tag_name as string | undefined;

    if (
      currentFilterTagName &&
      currentFilterTagName !== prevFilterTagNameRef.current &&
      prevFilterTagNameRef.current !== undefined
    ) {
      // filter_tag_name 变化时，不再走 analysisFilterParams（cond 请求）
      // 改由 MultiDataSourceTopChartsPage 监听 hotTagsOptions + filter_tag_name 变化后
      // 通过 handleHotTagConfirm 找到匹配选项并应用筛选值
      prevFilterTagNameRef.current = currentFilterTagName;
      // 清空上一次的 shortLinkFilterValue，避免旧值残留
      setShortLinkFilterValue(null);
    }
  }, [loading, history.location.query?.filter_tag_name]);

  return {
    urlCustomParams,
    shortLinkLoading,
    shortLinkValidQuery,
    shortLinkFilterValue,
  };
}
